/* istanbul ignore file */
import { applyProps, useThree } from '@react-three/fiber';
import { N8AOPostPass } from 'n8ao';
import { forwardRef, useLayoutEffect, useMemo } from 'react';
import { jsx } from 'react/jsx-runtime';
import { Color } from 'three';

export interface N8AOEffectProps {
  aoRadius?: number;
  aoSamples?: number;
  aoTones?: number;
  color?: Color;
  colorMultiply?: boolean;
  denoiseIterations?: number;
  denoiseRadius?: number;
  denoiseSamples?: number;
  depthAwareUpsampling?: boolean;
  distanceFalloff?: number;
  gammaCorrection?: boolean;
  halfRes?: boolean;
  intensity?: number;
  logarithmicDepthBuffer?: boolean;
  quality?: 'Performance' | 'Low' | 'Medium' | 'High' | 'Ultra';
  renderMode?: 0 | 1 | 2 | 3 | 4;
  screenSpaceRadius?: boolean;
  transparencyAware?: boolean;
}

const N8AOEffect = forwardRef<HTMLElement, N8AOEffectProps>(
  (
    {
      aoRadius = 5,
      aoSamples = 16,
      aoTones = 0,
      color = new Color(0, 0, 0),
      colorMultiply = true,
      denoiseIterations = 2.0,
      denoiseRadius = 12,
      denoiseSamples = 4,
      depthAwareUpsampling = true,
      distanceFalloff = 1,
      gammaCorrection = true,
      halfRes = false,
      intensity = 5,
      logarithmicDepthBuffer = true,
      quality = 'Medium',
      renderMode = 0,
      screenSpaceRadius = false,
      transparencyAware = true,
    },
    ref
  ) => {
    const { camera, scene } = useThree();
    const effect = useMemo(() => new N8AOPostPass(scene, camera), [camera, scene]);
    useLayoutEffect(() => {
      applyProps(effect.configuration, {
        aoRadius,
        aoSamples,
        aoTones,
        color,
        colorMultiply,
        denoiseIterations,
        denoiseRadius,
        denoiseSamples,
        depthAwareUpsampling,
        distanceFalloff,
        gammaCorrection,
        halfRes,
        intensity,
        logarithmicDepthBuffer,
        renderMode,
        screenSpaceRadius,
        transparencyAware,
      });
    }, [
      effect.configuration,
      aoRadius,
      aoSamples,
      aoTones,
      color,
      colorMultiply,
      denoiseIterations,
      denoiseRadius,
      denoiseSamples,
      depthAwareUpsampling,
      distanceFalloff,
      gammaCorrection,
      halfRes,
      intensity,
      logarithmicDepthBuffer,
      renderMode,
      screenSpaceRadius,
      transparencyAware,
    ]);

    useLayoutEffect(() => {
      if (quality) effect.setQualityMode(quality);
    }, [effect, quality]);
    return /* @__PURE__ */ jsx('primitive', { ref, object: effect });
  }
);
export { N8AOEffect };
